import { Nullable } from 'cng-common/models/utils'
import Logger from 'core/utils/Logger'

import { sortCampaignResourcesByCreatedDesc } from '../../lib'
import { CampaignDocument } from '../../model/campaign'
import {
  deleteCampaignAttachment,
  RelevantDocumentRaw,
  renameCampaignAttachment,
  updateCampaignAttachment,
  uploadCampaignAttachment,
  UploadDocument,
} from '../attachments'
import { campaignBaseApi } from '../campaignBaseApi'
import { mapDocumentFromRawData, mapLinkFromRawData } from '../mapping'
import {
  CampaignApiTags,
  GetCampaignDocumentsByCampaignIdParams,
  UpdateDocumentParams,
  UploadDocumentsParams,
} from '../models'
import {
  campaignProcedureUrl,
  SP_LUT_CAMPAIGN_DOCUMENTS_GET_BY_COMPANY,
  SP_LUT_CAMPAIGN_LINKS_GET_BY_COMPANY,
} from '../procedures'

const documentsEndpoints = campaignBaseApi.injectEndpoints({
  endpoints: (build) => ({
    // Fetch campaign documents and links
    // Documents (file uploads, form 2061) and links (their own table) live in
    // separate storage with separate procedures, but the UI shows them as one
    // combined list — so this query fetches both and merges, newest first.
    getCampaignDocumentsByCampaignId: build.query<
      { items: CampaignDocument[]; total: number },
      GetCampaignDocumentsByCampaignIdParams
    >({
      async queryFn({ campaignId }, _api, _extraOptions, baseQuery) {
        const data = { 'p.CampaignId': campaignId }

        const [documentsResult, linksResult] = await Promise.all([
          baseQuery({
            url: campaignProcedureUrl(SP_LUT_CAMPAIGN_DOCUMENTS_GET_BY_COMPANY),
            method: 'POST',
            data,
          }),
          baseQuery({
            url: campaignProcedureUrl(SP_LUT_CAMPAIGN_LINKS_GET_BY_COMPANY),
            method: 'POST',
            data,
          }),
        ])

        if (documentsResult.error) {
          return { error: documentsResult.error }
        }

        // Links failing must not blank the whole resources list — degrade to
        // documents only and log, so a partial backend outage stays partial.
        if (linksResult.error) {
          Logger.error('getCampaignDocumentsByCampaignId: failed to fetch links', linksResult.error)
        }

        const documents = (documentsResult.data?.Data || []).map(mapDocumentFromRawData)
        const links = (linksResult.data?.Data || []).map(mapLinkFromRawData)

        const items = sortCampaignResourcesByCreatedDesc([...documents, ...links])

        return { data: { items, total: items.length } }
      },

      providesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignDocuments, id: params.campaignId },
      ],
    }),

    // Upload documents
    // This endpoint uploads documents for a specific campaign.
    uploadDocuments: build.mutation<CampaignDocument[], UploadDocumentsParams>({
      async queryFn({ campaignId, countries, isVisible = true, files }) {
        const attachments: UploadDocument[] = files.map((file) => ({
          FileName: file.name,
          FileType: file.type,
          File: file,
          CampaignId: campaignId,
          Countries: countries.join(','),
          IsVisible: isVisible ? 1 : 0,
        }))

        const result = await Promise.allSettled(attachments.map(uploadCampaignAttachment))

        const successfulDocuments = result
          .filter((res) => res.status === 'fulfilled')
          .map((res) => mapDocumentFromRawData((res as PromiseFulfilledResult<unknown>).value))

        const failedDocuments = result
          .filter((res) => res.status === 'rejected')
          .map((res) => (res as PromiseRejectedResult).reason)

        if (failedDocuments.length > 0) {
          Logger.error('Failed to upload documents:', failedDocuments)
        }

        return { data: successfulDocuments }
      },

      invalidatesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignDocuments, id: params.campaignId },
      ],
    }),

    // Update document
    // This endpoint updates a document attachment row (form 2061).
    updateDocument: build.mutation<Nullable<CampaignDocument>, UpdateDocumentParams>({
      async queryFn(params) {
        try {
          const payload: RelevantDocumentRaw = {
            EntryId: params.entryId,
            FileName: params.fileName,
            FileType: params.fileType,
            FileLink: params.fileLink,
            Countries: params.countries.join(','),
            IsVisible: params.isVisible ? 1 : 0,
          }

          const response = await updateCampaignAttachment(payload)
          if (response && response.length > 0) {
            return { data: mapDocumentFromRawData(response[0]) }
          }
          return { data: null }
        } catch (error) {
          const message = 'updateDocument: Failed to update document'
          Logger.error(message, error)
          return { error }
        }
      },

      invalidatesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignDocuments, id: params.campaignId },
      ],
    }),

    // Delete document
    // This endpoint deletes a document attachment row (form 2061).
    deleteDocument: build.mutation<boolean, CampaignDocument>({
      async queryFn(document) {
        try {
          await deleteCampaignAttachment(document.entryId)
          return { data: true }
        } catch (error) {
          Logger.error('deleteDocument: Failed to delete document', error)
          return { error }
        }
      },

      invalidatesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignDocuments, id: params.campaignId },
      ],
    }),

    // Rename document
    // This endpoint renames a specific document (implemented as re-create;
    // see renameCampaignAttachment for the rollback semantics).
    renameDocument: build.mutation<CampaignDocument, CampaignDocument>({
      async queryFn(document) {
        try {
          const created = await renameCampaignAttachment(document)

          return { data: mapDocumentFromRawData(created) }
        } catch (error) {
          Logger.error('Error renaming document:', error)
          return { error }
        }
      },

      invalidatesTags: (_result, _error, params) => [
        { type: CampaignApiTags.CampaignDocuments, id: params.campaignId },
      ],
    }),
  }),
})

export const {
  useGetCampaignDocumentsByCampaignIdQuery,
  useUploadDocumentsMutation,
  useUpdateDocumentMutation,
  useDeleteDocumentMutation,
  useRenameDocumentMutation,
} = documentsEndpoints
